import React, { useEffect, useState } from 'react';
import { Modal, Button, Box, Tooltip, IconButton, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Switch, TablePagination } from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { Item } from '../../interfaces/item.interface';
import { ItemCategory } from '../../interfaces/item-category.interface';
import { UnitOfMeasurement } from '../../interfaces/unit-of-measurement.interface';
import { ItemService } from '../../services/item.service';
import { ItemCategoryService } from '../../services/item-category.service';
import { UnitOfMeasurementService } from '../../services/unitOfMeasurement.service';
import { useToast } from '../../context/ToastContext';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const ItemList: React.FC = () => {
    const [items, setItems] = useState<Item[]>([]);
    const [categories, setCategories] = useState<ItemCategory[]>([]);
    const [units, setUnits] = useState<UnitOfMeasurement[]>([]);
    const [loading, setLoading] = useState(true);
    const toast = useToast();
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);

    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [formData, setFormData] = useState({
        itemCode: '',
        itemName: '',
        description: '',
        itemCategoryId: '',
        unitOfMeasurementId: '',
        lotTracked: false,
        barcode: '',
        sellPrice: '',
        taxRatePct: '',
        posVisible: true
    });
    const [barcodeError, setBarcodeError] = useState('');
    const [missingSellPriceOnly, setMissingSellPriceOnly] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });
    const [pageSize, setPageSize] = useState(10);

    // L-7 fix: this list has no way to reach any item past page 0 without pagination -- a shop
    // with more unpriced items than one page can hold could never see the rest, which defeats
    // the whole point of a filter meant to surface every item the counter cannot sell.
    const fetchItems = async (page: number, search: string = '', missingSellPrice: boolean = false, size: number = pageSize) => {
        try {
            setLoading(true);
            const response = await ItemService.getItemsByInstitution(page, size, search, missingSellPrice);
            setItems(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err) {
            toast.showApiError(err, 'Failed to load items');
            console.error('Error fetching items:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (_: unknown, newPage: number) => {
        fetchItems(newPage, debouncedSearch, missingSellPriceOnly, pageSize);
    };

    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSize = parseInt(e.target.value, 10);
        setPageSize(newSize);
        fetchItems(0, debouncedSearch, missingSellPriceOnly, newSize);
    };

    const fetchCategories = async () => {
        try {
            const response = await ItemCategoryService.getItemCategoriesByInstitution(0, 100);
            setCategories(response.content);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const fetchUnits = async () => {
        try {
            const data = await UnitOfMeasurementService.getAll();
            setUnits(data);
        } catch (err) {
            console.error('Error fetching units:', err);
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchUnits();
    }, []);

    // Debounce the search term
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    // Fetch items on mount and whenever the debounced search or the missing-sell-price filter
    // changes (resetting to first page each time -- the filter narrows the whole catalog server
    // side now, not just whatever page happened to be loaded).
    useEffect(() => {
        fetchItems(0, debouncedSearch, missingSellPriceOnly, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, missingSellPriceOnly]);

    const emptyFormData = {
        itemCode: '',
        itemName: '',
        description: '',
        itemCategoryId: '',
        unitOfMeasurementId: '',
        lotTracked: false,
        barcode: '',
        sellPrice: '',
        taxRatePct: '',
        posVisible: true
    };

    const handleOpenFormModal = (item?: Item) => {
        setBarcodeError('');
        if (item) {
            setSelectedItem(item);
            setFormData({
                itemCode: item.itemCode,
                itemName: item.itemName,
                description: item.description,
                itemCategoryId: item.itemCategoryId,
                unitOfMeasurementId: item.unitOfMeasurementId || '',
                lotTracked: item.lotTracked,
                barcode: item.barcode || '',
                sellPrice: item.sellPrice !== undefined && item.sellPrice !== null ? String(item.sellPrice) : '',
                taxRatePct: item.taxRatePct !== undefined && item.taxRatePct !== null ? String(item.taxRatePct) : '',
                posVisible: item.posVisible !== undefined ? item.posVisible : true
            });
        } else {
            setSelectedItem(null);
            setFormData(emptyFormData);
        }
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedItem(null);
        setBarcodeError('');
        setFormData(emptyFormData);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setBarcodeError('');
        const payload = {
            ...formData,
            barcode: formData.barcode.trim() === '' ? undefined : formData.barcode.trim(),
            sellPrice: formData.sellPrice === '' ? undefined : Number(formData.sellPrice),
            taxRatePct: formData.taxRatePct === '' ? undefined : Number(formData.taxRatePct)
        };
        try {
            if (selectedItem) {
                await ItemService.updateItem(selectedItem.id!, payload);
            } else {
                await ItemService.createItem(payload);
            }
            await fetchItems(pageInfo.currentPage, debouncedSearch, missingSellPriceOnly, pageSize);
            handleCloseFormModal();
        } catch (error: any) {
            console.error('Error saving item:', error);
            const apiMessage = error?.response?.data;
            if (typeof apiMessage === 'string' && apiMessage.toLowerCase().includes('barcode')) {
                setBarcodeError(apiMessage);
            } else {
                toast.showApiError(error, 'Failed to save item');
            }
        }
    };

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Item List</h2>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => handleOpenFormModal()}
                >
                    Create Item
                </Button>
            </div>

            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                    sx={{ width: '300px' }}
                    label="Search by item name or code"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={missingSellPriceOnly}
                            onChange={(e) => setMissingSellPriceOnly(e.target.checked)}
                        />
                    }
                    label="Only items missing a sell price (these cannot be rung up at the counter)"
                />
            </Box>

            <div style={{ marginBottom: '20px' }}>
                Total Items: {pageInfo.totalElements}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="items-table">
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Item Code</th>
                        <th style={tableHeaderStyle}>Item Name</th>
                        <th style={tableHeaderStyle}>Description</th>
                        <th style={tableHeaderStyle}>Category</th>
                        <th style={tableHeaderStyle}>Unit</th>
                        <th style={tableHeaderStyle}>Barcode</th>
                        <th style={tableHeaderStyle}>Sell Price</th>
                        <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => (
                        <tr key={item.id} data-testid={`item-row-${item.id}`}>
                            <td style={tableCellStyle}>{item.itemCode}</td>
                            <td style={tableCellStyle}>{item.itemName}</td>
                            <td style={tableCellStyle}>{item.description}</td>
                            <td style={tableCellStyle}>{categories.find(c => c.id === item.itemCategoryId)?.itemCategoryName ?? ''}</td>
                            <td style={tableCellStyle}>{item.unit ? `${item.unit.name} (${item.unit.symbol})` : '-'}</td>
                            <td style={tableCellStyle}>{item.barcode || '-'}</td>
                            <td style={tableCellStyle}>
                                {item.sellPrice !== undefined && item.sellPrice !== null
                                    ? item.sellPrice
                                    : <span style={{ color: '#c62828' }}>Missing</span>}
                            </td>
                            <td style={tableCellStyle}>
                                <Tooltip title="Edit Item" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenFormModal(item)}
                                        color="primary"
                                        aria-label="Edit Item"
                                    >
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <TablePagination
                component="div"
                count={pageInfo.totalElements}
                page={pageInfo.currentPage}
                onPageChange={handlePageChange}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
            />

            {/* Form Modal */}
            <Modal
                open={isFormModalOpen}
                onClose={handleCloseFormModal}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1
                }}>
                    <h2>{selectedItem ? 'Edit Item' : 'Create Item'}</h2>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Item Code"
                            value={formData.itemCode}
                            onChange={(e) => setFormData({ ...formData, itemCode: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Item Name"
                            value={formData.itemName}
                            onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <FormControl fullWidth margin="normal" required>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={formData.itemCategoryId}
                                label="Category"
                                onChange={(e) => setFormData({ ...formData, itemCategoryId: e.target.value })}
                            >
                                {categories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.itemCategoryName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal" required>
                            <InputLabel>Unit of Measurement</InputLabel>
                            <Select
                                value={formData.unitOfMeasurementId}
                                label="Unit of Measurement"
                                onChange={(e) => setFormData({ ...formData, unitOfMeasurementId: e.target.value })}
                            >
                                {units.map((unit) => (
                                    <MenuItem key={unit.id} value={unit.id}>
                                        {unit.name} ({unit.symbol}){unit.isGlobal ? '' : ' — Custom'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.lotTracked}
                                    onChange={(e) => setFormData({ ...formData, lotTracked: e.target.checked })}
                                />
                            }
                            label="Lot Tracked"
                        />
                        <TextField
                            fullWidth
                            label="Barcode"
                            value={formData.barcode}
                            onChange={(e) => {
                                setBarcodeError('');
                                setFormData({ ...formData, barcode: e.target.value });
                            }}
                            margin="normal"
                            error={!!barcodeError}
                            helperText={barcodeError || 'Optional — used for counter scanning'}
                        />
                        <TextField
                            fullWidth
                            type="number"
                            label="Sell Price"
                            value={formData.sellPrice}
                            onChange={(e) => setFormData({ ...formData, sellPrice: e.target.value })}
                            margin="normal"
                            inputProps={{ step: '0.01', min: 0 }}
                            helperText="Required before this item can be rung at the counter or synced to POS"
                        />
                        <TextField
                            fullWidth
                            type="number"
                            label="Tax Rate (%)"
                            value={formData.taxRatePct}
                            onChange={(e) => setFormData({ ...formData, taxRatePct: e.target.value })}
                            margin="normal"
                            inputProps={{ step: '0.01', min: 0, max: 100 }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.posVisible}
                                    onChange={(e) => setFormData({ ...formData, posVisible: e.target.checked })}
                                />
                            }
                            label="Visible at POS counter"
                        />
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={handleCloseFormModal}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" color="primary">
                                {selectedItem ? 'Update' : 'Create'}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>


        </div>
    );
};

export default ItemList;
